/**
 * Search Component
 */
import React from 'react';
import { useState, useCallback, useEffect } from 'react';
import Input from '../Input/Input';
import styles from './Search.module.css';

export interface SearchProps {
  value?: string;
  onSearch: (searchTerm: string) => void;
  className?: string;
  placeholder?: string;
}

function Search({
    value,
    onSearch,
    className,
    placeholder
}: SearchProps) {
    const [searchTerm, setSearchTerm] = useState(value || '');
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

    // Debounced search
    const handleSearchChange = useCallback((value: string) => {
        setSearchTerm(value);
    
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
    
        const timeout = setTimeout(() => {
            onSearch(value);
        }, 300);
    
        setSearchTimeout(timeout);
    }, [onSearch, searchTimeout]);

    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    const clearSearch = () => {
        setSearchTerm('');
        onSearch('');
    };

    return (
        <div className={`${styles.container} ${className || ''}`}>
            <Input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={placeholder}
                variant="search"
                rightIcon={
                    searchTerm ? (
                        <button
                            type="button"
                            onClick={clearSearch}
                            className={styles.clearButton}
                            aria-label="Clear search"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8"></circle>
                            <path d="m21 21-4.35-4.35"></path>
                        </svg>
                    )
                }
            />
        </div>
    );
}

export default React.memo(Search);
